import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuthStore } from '../store';
import { formatDistanceToNow } from '../lib/utils';
import PostCard from '../components/post/PostCard';

interface Comment {
  id: string;
  content: string;
  author: {
    id: string;
    displayName: string;
    handle: string;
    avatarUrl?: string;
  };
  createdAt: string;
  replyCount: number;
  replies?: Comment[];
}

interface PostDetail {
  id: string;
  content: string;
  author: {
    id: string;
    displayName: string;
    handle: string;
    avatarUrl?: string;
  };
  segment?: {
    id: string;
    name: string;
    canalName: string;
  };
  media: Array<{
    id: string;
    type: 'image' | 'video';
    url: string;
    thumbnailUrl?: string;
  }>;
  weather?: {
    temp: number;
    condition: string;
    icon: string;
  };
  stats: {
    reactions: number;
    comments: number;
    shares: number;
  };
  userReaction?: string;
  createdAt: string;
}

export default function PostPage() {
  const { id: postId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [post, setPost] = useState<PostDetail | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentContent, setCommentContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  
  useEffect(() => {
    const loadPost = async () => {
      if (!postId) return;
      
      setLoading(true);
      try {
        const [postRes, commentsRes] = await Promise.all([
          api.posts.getById(postId),
          api.posts.getComments(postId),
        ]);
        setPost(postRes);
        setComments(commentsRes);
      } catch (err) {
        console.error('Failed to load post:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    
    loadPost();
  }, [postId, navigate]);
  
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentContent.trim() || !postId) return;
    
    setSubmitting(true);
    try {
      const newComment = await api.posts.addComment(postId, { content: commentContent });
      setComments([newComment, ...comments]);
      setCommentContent('');
      if (post) {
        setPost({ ...post, stats: { ...post.stats, comments: post.stats.comments + 1 } });
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSubmitReply = async (commentId: string) => {
    if (!replyContent.trim() || !postId) return;
    
    setSubmitting(true);
    try {
      const newReply = await api.posts.addComment(postId, {
        content: replyContent,
        parentId: commentId,
      });
      setComments(comments.map((c) => {
        if (c.id === commentId) {
          return {
            ...c,
            replyCount: c.replyCount + 1,
            replies: [...(c.replies || []), newReply],
          };
        }
        return c;
      }));
      setReplyingTo(null);
      setReplyContent('');
    } catch (err) {
      console.error('Failed to add reply:', err);
    } finally {
      setSubmitting(false);
    }
  };
  
  const loadReplies = async (commentId: string) => {
    try {
      const replies = await api.posts.getCommentReplies(postId!, commentId);
      setComments(comments.map((c) => {
        if (c.id === commentId) {
          return { ...c, replies };
        }
        return c;
      }));
    } catch (err) {
      console.error('Failed to load replies:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="card p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto" />
        </div>
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="card p-8 text-center">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Post not found</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-4">This post may have been deleted.</p>
          <Link to="/" className="btn btn-primary">Go home</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto p-4 pb-24">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 mb-4"
      >
        <BackIcon className="w-5 h-5" />
        <span>Back</span>
      </button>
      
      {/* Post */}
      <PostCard post={post} showFullContent />
      
      {/* Comment form */}
      <div className="card p-4 mt-4">
        <form onSubmit={handleSubmitComment}>
          <div className="flex gap-3">
            <div className="avatar">
              {user?.displayName?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1">
              <textarea
                value={commentContent}
                onChange={(e) => setCommentContent(e.target.value)}
                placeholder="Write a comment..."
                className="input min-h-[80px] resize-none"
                rows={3}
              />
              <div className="flex justify-end mt-2">
                <button
                  type="submit"
                  disabled={!commentContent.trim() || submitting}
                  className="btn btn-primary"
                >
                  {submitting ? 'Posting...' : 'Comment'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      
      {/* Comments */}
      <div className="mt-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Comments ({post.stats.comments})
        </h2>
        
        {comments.length === 0 ? (
          <div className="card p-8 text-center">
            <CommentIcon className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400">No comments yet. Be the first!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="card p-4">
                <div className="flex gap-3">
                  <Link to={`/@${comment.author.handle}`}>
                    <div className="avatar">
                      {comment.author.displayName.charAt(0).toUpperCase()}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/@${comment.author.handle}`}
                        className="font-medium text-slate-900 dark:text-white hover:underline"
                      >
                        {comment.author.displayName}
                      </Link>
                      <span className="text-xs text-slate-400">
                        {formatDistanceToNow(new Date(comment.createdAt))}
                      </span>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300 mt-1 whitespace-pre-wrap">
                      {comment.content}
                    </p>
                    
                    <div className="flex items-center gap-4 mt-2">
                      <button
                        onClick={() => {
                          setReplyingTo(replyingTo === comment.id ? null : comment.id);
                          setReplyContent('');
                        }}
                        className="text-sm text-slate-500 hover:text-primary-500"
                      >
                        Reply
                      </button>
                      {comment.replyCount > 0 && !comment.replies && (
                        <button
                          onClick={() => loadReplies(comment.id)}
                          className="text-sm text-primary-500 hover:text-primary-600"
                        >
                          View {comment.replyCount} {comment.replyCount === 1 ? 'reply' : 'replies'}
                        </button>
                      )}
                    </div>
                    
                    {/* Reply form */}
                    {replyingTo === comment.id && (
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={replyContent}
                          onChange={(e) => setReplyContent(e.target.value)}
                          placeholder="Write a reply..."
                          className="input flex-1"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSubmitReply(comment.id)}
                          disabled={!replyContent.trim() || submitting}
                          className="btn btn-primary btn-sm"
                        >
                          Reply
                        </button>
                      </div>
                    )}
                    
                    {/* Replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="mt-3 space-y-3 pl-4 border-l-2 border-slate-100 dark:border-slate-700">
                        {comment.replies.map((reply) => (
                          <div key={reply.id} className="flex gap-2">
                            <Link to={`/@${reply.author.handle}`}>
                              <div className="avatar avatar-sm">
                                {reply.author.displayName.charAt(0).toUpperCase()}
                              </div>
                            </Link>
                            <div>
                              <div className="flex items-center gap-2">
                                <Link
                                  to={`/@${reply.author.handle}`}
                                  className="text-sm font-medium text-slate-900 dark:text-white hover:underline"
                                >
                                  {reply.author.displayName}
                                </Link>
                                <span className="text-xs text-slate-400">
                                  {formatDistanceToNow(new Date(reply.createdAt))}
                                </span>
                              </div>
                              <p className="text-sm text-slate-700 dark:text-slate-300">
                                {reply.content}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function CommentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}
